import { each, first, get, isEmpty, isArray, map } from 'lodash';

import {
    SERVICE_TESTS_URL,
    TEST_TYPES_URL,
    ADMIN_TESTS_UPDATE_SELECTION_URL,
    SERVICE_TESTS_DELETE_URL,
} from './TestTypesAdminConstants';

const FALLBACK_LIMIT = 10000;
const TEST_COUNTS_URL = '/ipm/v1/admin/tests/testCounts';

function normalizeListResponse(response) {
    if (isArray(response)) {
        return response;
    }

    if (isArray(response?.data)) {
        return response.data;
    }

    if (isArray(response?.data?.data)) {
        return response.data.data;
    }

    if (isArray(response?.results)) {
        return response.results;
    }

    return [];
}

export async function getAllTestTypes(dataService) {
    const response = await dataService.get(TEST_TYPES_URL, {
        lean: true,
        rowLimit: FALLBACK_LIMIT,
        sort: 'name',
    });

    return normalizeListResponse(response);
}

export async function getAllServiceTests(dataService) {
    const response = await dataService.get(SERVICE_TESTS_URL, {
        rowLimit: FALLBACK_LIMIT,
        sort: 'name',
    });

    return normalizeListResponse(response);
}

/**
 * Fetches test counts grouped by testType group and status.
 * Mirrors portal/ui TestsService.queryTestsCount() + the aggregation in testTypesList.directive.js
 *
 * API response shape: [{ _id: { type, status }, count, testType: [{ group }] }, ...]
 * Returns: { [groupName]: { all: number, enabled: number } }
 */
export async function getTestCounts(dataService) {
    const response = await dataService.get(TEST_COUNTS_URL);
    const testCounts = normalizeListResponse(response);

    const sum = {};
    each(testCounts, (count) => {
        const group = get(first(count.testType), 'group');
        if (isEmpty(group)) return;

        if (!sum[group]) {
            sum[group] = { all: 0, enabled: 0 };
        }
        sum[group].all += count.count || 0;

        if (get(count, '_id.status') === 'Running') {
            sum[group].enabled += count.count || 0;
        }
    });

    return sum;
}

/**
 * Check whether a specific test type (by name) has any test instances.
 * Uses the same testCounts endpoint as the list page, but aggregates per testType name.
 * Mirrors portal/ui's lookForTestInstances → getTestInstanceCount.
 *
 * @returns {boolean} true if the test type has at least one test instance
 */
export async function hasTestInstancesForType(dataService, testTypeName) {
    if (!testTypeName) return false;

    const response = await dataService.get(TEST_COUNTS_URL);
    const testCounts = normalizeListResponse(response);

    let total = 0;
    each(testCounts, (entry) => {
        const entryTypeName = get(first(entry.testType), 'name');
        if (entryTypeName === testTypeName) {
            total += entry.count || 0;
        }
    });

    return total > 0;
}

export async function updateTestSelection(dataService, requestParams, selectionParams, updateDoc) {
    const testTypes = map(selectionParams.selection, 'testType._id');
    const queryParams = { ...requestParams, testTypes };
    return dataService.post(
        ADMIN_TESTS_UPDATE_SELECTION_URL,
        { selectionParams, updateDoc, allTestTypes: true },
        queryParams
    );
}

export async function disableTestsForType(dataService, testTypeId) {
    if (isEmpty(testTypeId)) {
        return null;
    }

    return dataService.post(
        ADMIN_TESTS_UPDATE_SELECTION_URL,
        {
            selectionParams: {
                inverted: true,
                selection: [],
                valid: true,
            },
            updateDoc: {
                status: 'Disabled',
            },
        },
        {
            testTypes: [testTypeId],
        }
    );
}

export async function deleteManyTests(dataService, requestParams, selectionParams) {
    return dataService.remove(SERVICE_TESTS_DELETE_URL, { selectionParams }, requestParams);
}

/**
 * Convert an uploaded Python script to a python 2/3 compatible version.
 * Mirrors portal/ui TestTypes.convertScript resource call.
 *
 * @returns {Promise<{ code: number, message?: string, script?: string, stepMetrics?: any[] }>}
 */
export async function convertScript(dataService, script) {
    return dataService.post('/ipm/v1/admin/testTypes/convertScript', { script });
}
